import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState(() => {
    try {
      const items = JSON.parse(localStorage.getItem('shopping_cart') || '[]');
      return Array.isArray(items) ? items.filter(i => i && i.product_id && Number.isFinite(Number(i.price)) && Number(i.quantity) > 0).map(i => ({ ...i, quantity: Number(i.quantity), price: Number(i.price), max_quantity: Number(i.max_quantity ?? 10) })) : [];
    } catch { return []; }
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('shopping_cart', JSON.stringify(cart));
    } catch (error) {
      console.error('Error saving cart to localStorage:', error);
    }
  }, [cart]);

  const addToCart = useCallback((product, variation = null, quantity = 1) => {
    if (!product || !product.product_id || quantity < 1 || (variation?.stock ?? product.stock ?? product.quantity ?? 0) < 1) {
      console.error('Invalid product data');
      return false;
    }

    setCart(prevCart => {
      const variationId = variation?.variation_id || null;
      const itemId = `${product.product_id}-${variationId || 'base'}`;
      
      const existingItemIndex = prevCart.findIndex(item => item.id === itemId);

      if (existingItemIndex >= 0) {
        // Update existing item
        const updatedCart = [...prevCart];
        const currentItem = updatedCart[existingItemIndex];
        const newQuantity = (currentItem.quantity || 1) + quantity;
        const maxQuantity = currentItem.max_quantity || 10;
        
        updatedCart[existingItemIndex] = {
          ...currentItem,
          quantity: Math.min(newQuantity, maxQuantity)
        };
        
        // Show success message
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        
        return updatedCart;
      } else {
        // Add new item
        const maxQuantity = variation?.stock ?? product.stock ?? product.quantity ?? 0;
        const newItem = {
          product_id: product.product_id,
          variation_id: variationId,
          name: product.name || 'Product',
          color: variation?.color?.name || variation?.color || null,
          size: variation?.size?.name || variation?.size || null,
          price: parseFloat(variation?.price || product.base_price || product.price || 0),
          image: product.main_image || (product.images && product.images[0]) || product.image || '',
          quantity: Math.min(quantity, maxQuantity),
          max_quantity: maxQuantity,
          id: itemId
        };
        
        // Show success message
        setShowSuccessMessage(true);
        setTimeout(() => setShowSuccessMessage(false), 3000);
        
        return [...prevCart, newItem];
      }
    });
    
    return true;
  }, []);

  // FIXED: Remove item by id
  const removeFromCart = useCallback((productId, variationId = null) => {
    setCart(prevCart => {
      let itemId;
      
      if (typeof productId === 'string' && productId.includes('-')) {
        // If passed as full id
        itemId = productId;
      } else {
        // If passed as product_id + variation_id
        itemId = `${productId}-${variationId || 'base'}`;
      }
      
      return prevCart.filter(item => item.id !== itemId);
    });
  }, []);

  // FIXED: Update quantity
  const updateQuantity = useCallback((productId, variationId, newQuantity) => {
    if (newQuantity < 1) return;
    
    setCart(prevCart => 
      prevCart.map(item => {
        let itemId;
        
        if (typeof productId === 'string' && productId.includes('-')) {
          itemId = productId;
        } else {
          itemId = `${productId}-${variationId || 'base'}`;
        }
        
        if (item.id === itemId) {
          const maxQty = item.max_quantity || 10;
          return { 
            ...item, 
            quantity: Math.max(1, Math.min(newQuantity, maxQty))
          };
        }
        return item;
      })
    );
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
  }, []);

  // Calculate totals
  const cartTotal = cart.reduce((sum, item) => 
    sum + (parseFloat(item.price || 0) * (item.quantity || 1)), 0
  );
  
  const cartItemCount = cart.reduce((count, item) => 
    count + (item.quantity || 1), 0
  );

  // Success message component
  const SuccessMessage = () => {
    if (!showSuccessMessage) return null;
    
    return (
      <div className="fixed top-4 right-4 z-50 animate-fade-in">
        <div className="bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg flex items-center space-x-2">
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          <span>Product added to cart successfully!</span>
        </div>
      </div>
    );
  };

  return (
    <CartContext.Provider value={{
      cart,
      addToCart,
      removeFromCart,
      updateQuantity,
      clearCart,
      cartTotal,
      cartItemCount,
      showSuccessMessage
    }}>
      {children}
      <SuccessMessage />
    </CartContext.Provider>
  );
};

CartProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};